import axios, { type AxiosInstance } from "axios";
import type { DashboardRow, RemediationJob, RepoDetail, ScanResult } from "../types";

const API_BASE = process.env.VUE_APP_CVE_ASSISTANT_API_URL || "http://localhost:8080/api/v1";

let authGetter: (() => Promise<string>) | null = null;

export function configureAuth(getJwt: () => Promise<string>): void {
  authGetter = getJwt;
}

function createClient(): AxiosInstance {
  const client = axios.create({ baseURL: API_BASE, timeout: 300000 });
  client.interceptors.request.use(async (config) => {
    if (authGetter) {
      const token = await authGetter();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  return client;
}

const api = createClient();

export async function fetchVulnerabilities(refresh = false): Promise<DashboardRow[]> {
  const { data } = await api.get<DashboardRow[]>("/vulnerabilities", {
    params: refresh ? { refresh: true } : undefined,
  });
  return data;
}

export async function fetchRepoDetail(repo: string): Promise<RepoDetail> {
  const { data } = await api.get<RepoDetail>(`/vulnerabilities/${repo}`);
  return data;
}

export async function triggerScan(): Promise<ScanResult> {
  const { data } = await api.post<ScanResult>("/scans", {});
  return data;
}

export async function createRemediationMr(repo: string): Promise<RemediationJob> {
  const { data } = await api.post<RemediationJob>(`/repos/${repo}/remediation-mrs`);
  return data;
}

export async function getRemediationJob(jobId: string): Promise<RemediationJob> {
  const { data } = await api.get<RemediationJob>(`/remediation-jobs/${jobId}`);
  return data;
}

export async function pollRemediationJob(
  jobId: string,
  onUpdate?: (job: RemediationJob) => void,
  maxAttempts = 30,
  intervalMs = 2000
): Promise<RemediationJob> {
  let attempts = 0;
  while (attempts < maxAttempts) {
    const job = await getRemediationJob(jobId);
    onUpdate?.(job);
    if (job.status === "opened" || job.status === "failed") {
      return job;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    attempts += 1;
  }
  return getRemediationJob(jobId);
}
